import { useCallback, useEffect, useRef, useState, type TransitionEvent } from 'react';
import { useUiStore } from '@/shared/lib/uiStore';

export type RevealState = 'collapsed' | 'opening' | 'open' | 'closing';

const DEADZONE = 320;
const OPEN_DISTANCE = 780;
const SNAP_THRESHOLD = 0.32;
const WHEEL_IDLE = 130;
const PROX_RADIUS = 320;

const FULL_PULL = DEADZONE + OPEN_DISTANCE;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function canOpenFrom(el: HTMLElement | null): boolean {
  let node: HTMLElement | null = el;
  let nearest: HTMLElement | null = null;
  let outermost: HTMLElement | null = null;
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
      if (!nearest) nearest = node;
      outermost = node;
    }
    node = node.parentElement;
  }
  if (!nearest) return (document.scrollingElement?.scrollTop ?? 0) <= 0;
  return nearest === outermost && nearest.scrollTop <= 0;
}

function isInChatArea(el: EventTarget | null): boolean {
  return el instanceof Element && !!el.closest('[data-ai-scroll]');
}

export function useRevealController(enabled: boolean) {
  const rootRef = useRef<HTMLDivElement>(null);
  const skyRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<RevealState>('collapsed');

  const progress = useRef(0);
  const hint = useRef(0);
  const pullPx = useRef(0);
  const dragging = useRef(false);
  const animating = useRef(false);
  const rafId = useRef(0);

  const flush = useCallback(() => {
    const sky = skyRef.current;
    const shell = shellRef.current;
    if (sky) {
      sky.style.setProperty('--p', String(progress.current));
      sky.style.setProperty('--pull', String(hint.current));
    }
    if (shell) {
      const atRest = hint.current === 0 && progress.current === 0;
      shell.style.transform = atRest
        ? ''
        : `translateY(calc(${hint.current * 96}px + ${progress.current} * 100lvh))`;
      shell.style.willChange = atRest ? '' : 'transform';
    }
  }, []);

  const paint = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      flush();
    });
  }, [flush]);

  const setDragging = useCallback((on: boolean) => {
    dragging.current = on;
    rootRef.current?.toggleAttribute('data-dragging', on);
  }, []);

  const applyPull = useCallback(
    (px: number) => {
      pullPx.current = px;
      hint.current = clamp01(px / DEADZONE);
      progress.current = clamp01((px - DEADZONE) / OPEN_DISTANCE);
      paint();
    },
    [paint],
  );

  const applyProgress = useCallback(
    (p: number) => {
      progress.current = p;
      hint.current = 1;
      pullPx.current = DEADZONE + p * OPEN_DISTANCE;
      paint();
    },
    [paint],
  );

  const snapTo = useCallback(
    (target: 0 | 1) => {
      setDragging(false);
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;

      const tp = target;
      const th = target;
      const tpx = target === 1 ? FULL_PULL : 0;
      const willAnimate = progress.current !== tp || hint.current !== th;

      progress.current = tp;
      hint.current = th;
      pullPx.current = tpx;

      if (prefersReducedMotion() || !willAnimate) {
        flush();
        animating.current = false;
        setState(target === 1 ? 'open' : 'collapsed');
        return;
      }
      animating.current = true;
      setState(target === 1 ? 'opening' : 'closing');
      flush();
    },
    [setDragging, flush],
  );

  const open = useCallback(() => snapTo(1), [snapTo]);
  const close = useCallback(() => snapTo(0), [snapTo]);

  useEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) return;

    let startY = 0;
    let startP = 0;
    let mode: 'open' | 'close' | null = null;
    let wheelTimer = 0;

    const releaseSnap = () => snapTo(progress.current > SNAP_THRESHOLD ? 1 : 0);

    const onTouchStart = (e: TouchEvent) => {
      if (animating.current) return;
      if (progress.current > 0.5) {
        if (isInChatArea(e.target)) return;
        mode = 'close';
        startP = progress.current;
      } else if (canOpenFrom(e.target as HTMLElement)) {
        mode = 'open';
        pullPx.current = 0;
      } else {
        return;
      }
      startY = e.touches[0].clientY;
      setDragging(true);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!mode) return;
      const dy = e.touches[0].clientY - startY;
      if (mode === 'open') {
        applyPull(Math.max(0, dy));
        if (dy > 0) e.preventDefault();
      } else {
        applyProgress(clamp01(startP + dy / OPEN_DISTANCE));
        e.preventDefault();
      }
    };
    const onTouchEnd = () => {
      if (!mode) return;
      mode = null;
      releaseSnap();
    };

    const scheduleRelease = () => {
      clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(() => {
        if (dragging.current) releaseSnap();
      }, WHEEL_IDLE);
    };
    const onWheel = (e: WheelEvent) => {
      if (animating.current) return;
      const up = e.deltaY < 0;

      if (progress.current > 0.5) {
        if (!up && !dragging.current && isInChatArea(e.target)) {
          return;
        }
        if (!up) {
          e.preventDefault();
          if (!dragging.current) setDragging(true);
          applyProgress(clamp01(progress.current - e.deltaY / OPEN_DISTANCE));
          scheduleRelease();
        }
        return;
      }
      if (up && canOpenFrom(e.target as HTMLElement)) {
        e.preventDefault();
        if (!dragging.current) setDragging(true);
        applyPull(pullPx.current + -e.deltaY);
        scheduleRelease();
      } else if (!up && pullPx.current > 0) {
        e.preventDefault();
        applyPull(Math.max(0, pullPx.current - e.deltaY));
        scheduleRelease();
      }
    };

    let prox = 0;
    let proxRaf = 0;
    const canHover = window.matchMedia?.('(hover: hover)').matches ?? false;
    const writeProx = (v: number) => {
      if (v === prox) return;
      prox = v;
      if (proxRaf) return;
      proxRaf = requestAnimationFrame(() => {
        proxRaf = 0;
        skyRef.current?.style.setProperty('--prox', String(prox));
      });
    };
    const onPointerMove = (e: PointerEvent) => {
      if (animating.current || dragging.current || progress.current > 0.01 || pullPx.current > 0) {
        writeProx(0);
        return;
      }
      const dx = e.clientX - window.innerWidth / 2;
      const dy = Math.max(0, e.clientY);
      writeProx(clamp01(1 - Math.hypot(dx, dy) / PROX_RADIUS));
    };
    const onPointerLeave = () => writeProx(0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && progress.current > 0.5 && !useUiStore.getState().mobileNavOpen) close();
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', onTouchEnd);
    window.addEventListener('keydown', onKey);
    if (canHover) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      document.documentElement.addEventListener('pointerleave', onPointerLeave);
    }

    return () => {
      clearTimeout(wheelTimer);
      cancelAnimationFrame(rafId.current);
      cancelAnimationFrame(proxRaf);
      rafId.current = 0;
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [enabled, applyPull, applyProgress, setDragging, snapTo, close]);

  const onTransitionEnd = useCallback((e: TransitionEvent) => {
    if ((e.propertyName !== '--p' && e.propertyName !== '--pull') || dragging.current) return;
    animating.current = false;
    setState(progress.current > 0.5 ? 'open' : 'collapsed');
  }, []);

  return { rootRef, skyRef, shellRef, state, open, close, onTransitionEnd };
}
